import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import { Dropdown, DropdownToggle, DropdownMenu, Row, Col, Button } from "reactstrap";
import SimpleBar from "simplebar-react";
import { getFirestore, collection, onSnapshot, query, where, doc, updateDoc, getDocs, setDoc } from "firebase/firestore";
import { db } from "helpers/firebase/firebaseConfig";
import Cookies from "js-cookie";
import { fetchOrdersRequest } from "store/actions";
import { withTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { Modal, ModalHeader, ModalBody } from "reactstrap";
import { format, parseISO } from 'date-fns';
import { ar } from 'date-fns/locale';

const NotificationDropdown = props => {
  const [modal, setModal] = useState(false);
  const [selectedNotification, setSelectedNotification] = useState(null);
  const [menu, setMenu] = useState(false);
  
  const orders = useSelector(state => state.orders.orders);
  const unseenCount = orders.filter(o => !o.seen).length;
  const badgeCount = unseenCount;

  const userId = Cookies.get("userId");
  const dispatch = useDispatch();

  const sortedNotifications = [...orders].sort((a, b) => 
    new Date(b.created_at?.seconds * 1000 || 0) - new Date(a.created_at?.seconds * 1000 || 0)
  );

  useEffect(() => {
    if (!userId) return;

    const ordersQuery = query(
      collection(db, "Booking"),
      where("seen", "==", false)
    );

    const unsubscribe = onSnapshot(ordersQuery, () => {
      dispatch(fetchOrdersRequest());
    }, (error) => {
      console.error("Error listening to orders:", error);
    });

    return () => unsubscribe();
  }, [userId, dispatch]);

  const markNotificationSeen = async (orderId) => {
    try {
      await setDoc(doc(db, "Booking", orderId), { seen: true }, { merge: true });
      dispatch(fetchOrdersRequest());
    } catch (err) {
      if (err.code !== "not-found") console.error("Failed to mark notification seen:", err);
    }
  };

  const markAllSeen = async () => {
    try {
      const q = query(collection(db, "Booking"), where("seen", "==", false));
      const snap = await getDocs(q);
      const updates = snap.docs.map(d =>
        setDoc(d.ref, { seen: true }, { merge: true }).catch(err => {
          if (err.code !== "not-found") console.error("Failed to mark notification seen:", err);
        })
      );
      await Promise.all(updates);
      dispatch(fetchOrdersRequest());
    } catch (err) {
      console.error("Failed to mark all notifications seen:", err);
    }
  };

  const handleNotificationClick = (notification) => {
    setSelectedNotification(notification);
    markNotificationSeen(notification.id);
    setModal(true);
  };

  const toggleModal = () => {
    if (selectedNotification && !selectedNotification.seen) {
      setSelectedNotification(null);
    }
    setModal(!modal);
  };

  const formatFirebaseDate = (timestamp) => {
    if (!timestamp?.seconds) return 'N/A';
    const date = new Date(timestamp.seconds * 1000);
    return format(date, 'MMMM dd, yyyy h:mm a', { locale: ar });
  };

  const getServiceDetails = (order) => {
    return order.serviceProviderServices?.name || 
           order.service_name || 
           'Service details not available';
  };

  const getCustomerDetails = (order) => {
    return order.app_user_id?.username || 
           order.customer_name || 
           'Customer details not available';
  };

  const getPaymentStatus = (order) => {
    return order.paymentStatus === 'paid' ? 'مدفوعة' : 'غير مدفوعة';
  };

  const getPaymentMethod = (order) => {
    return order.paymentMethod === 'cash' ? 'نقداً' : 
           order.paymentMethod === 'card' ? 'بطاقة' : 
           'طريقة دفع غير معروفة';
  };

  const getTotalAmount = (order) => {
    return order.total_after_discount || order.total || 'N/A';
  };

  return (
    <React.Fragment>
      <Dropdown
        isOpen={menu}
        toggle={() => setMenu(!menu)}
        className="dropdown d-inline-block"
        tag="li"
      >
        <DropdownToggle 
          className="btn header-item noti-icon position-relative" 
          tag="button" 
          id="page-header-notifications-dropdown"
        >
          <i className="bx bx-bell bx-tada" />
          <span className="badge bg-danger rounded-pill">{badgeCount}</span>
        </DropdownToggle>

        <DropdownMenu className="dropdown-menu dropdown-menu-lg dropdown-menu-end p-0">
          <div className="p-3">
            <Row className="align-items-center">
              <Col>
                <h6 className="m-0"> {props.t("إشعارات الطلبات")} </h6>
              </Col>
              <div className="col-auto d-flex gap-2">
                <Button color="link" size="sm" className="p-0" onClick={markAllSeen}>
                  {props.t("تعيين الكل كمقروء")}
                </Button>
                <a href="#" className="small" onClick={() => setModal(true)}>
                  {props.t("عرض الكل")}
                </a>
              </div>
            </Row>
          </div>

          <SimpleBar style={{ height: "230px" }}>
            {orders
              .filter(order => !order.seen)
              .map(order => (
                <div
                  className="text-reset notification-item"
                  key={order.id}
                  onClick={() => handleNotificationClick(order)}
                >
                  <div className="d-flex">
                    <div className="avatar-xs me-3">
                      <span className="avatar-title bg-primary rounded-circle font-size-16">
                        <i className="bx bx-cart" />
                      </span>
                    </div>
                    <div className="flex-grow-1">
                      <h6 className="mt-0 mb-1">
                        طلب جديد من {getCustomerDetails(order)}
                      </h6>
                      <div className="font-size-12 text-muted">
                        <p className="mb-1">
                          الخدمة: {getServiceDetails(order)}<br />
                          المبلغ: {getTotalAmount(order)} ر.س<br />
                          حالة الدفع: {getPaymentStatus(order)}
                        </p>
                        {/* <p className="mb-0">
                          <i className="mdi mdi-clock-outline" />
                          {formatFirebaseDate(order.created_at)}
                        </p> */}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
          </SimpleBar>
        </DropdownMenu>
      </Dropdown>

      <Modal isOpen={modal} toggle={toggleModal} size="lg">
        <ModalHeader toggle={toggleModal}>
          {selectedNotification ? "تفاصيل الطلب" : props.t("جميع الإشعارات")}
        </ModalHeader>
        <ModalBody>
          {selectedNotification ? (
            <div>
              <h4>تفاصيل الطلب #{selectedNotification.id}</h4>
              <div className="mb-3">
                <h6>العميل: {getCustomerDetails(selectedNotification)}</h6>
                <p className="text-muted">
                  {selectedNotification.comment && `ملاحظات: ${selectedNotification.comment}`}
                </p>
              </div>
              
              <div className="mb-3">
                <h6>الخدمة المطلوبة</h6>
                <p>{getServiceDetails(selectedNotification)}</p>
                {selectedNotification.serviceProviderServices?.description && (
                  <p className="text-muted">
                    {selectedNotification.serviceProviderServices.description}
                  </p>
                )}
              </div>
              
              <div className="row">
                <div className="col-md-6">
                  <h6>المعلومات المالية</h6>
                  <p>
                    الإجمالي: {getTotalAmount(selectedNotification)} ر.س<br />
                    طريقة الدفع: {getPaymentMethod(selectedNotification)}<br />
                    حالة الدفع: {getPaymentStatus(selectedNotification)}
                  </p>
                </div>
                <div className="col-md-6">
                  <h6>معلومات الحجز</h6>
                  <p>
                    حالة الطلب: {selectedNotification.booking_status}<br />
                    {/* تاريخ الإنشاء: {formatFirebaseDate(selectedNotification.created_at)} */}
                  </p>
                </div>
              </div>
            </div>
          ) : (
            <SimpleBar style={{ height: "400px" }}>
              {sortedNotifications
                .filter(notification => !notification.seen)
                .map(notification => (
                  <div 
                    key={notification.id} 
                    className={`mb-3 p-3 ${notification.booking_status === 'pending' ? 'bg-light' : ''}`}
                    onClick={() => handleNotificationClick(notification)}
                    style={{ cursor: 'pointer' }}
                  >
                    <div className="d-flex justify-content-between">
                      <h6>طلب #{notification.id}</h6>
                      <span className={`badge ${notification.booking_status === 'pending' ? 'bg-warning' : 'bg-success'}`}>
                        {notification.booking_status}
                      </span>
                    </div>
                    <p className="mb-1">
                      العميل: {getCustomerDetails(notification)}<br />
                      الخدمة: {getServiceDetails(notification)}
                    </p>
                    <p className="text-muted mb-1">
                      المبلغ: {getTotalAmount(notification)} ر.س | 
                      الدفع: {getPaymentStatus(notification)} | 
                      {formatFirebaseDate(notification.created_at)}
                    </p>
                  </div>
                ))}
            </SimpleBar>
          )}
        </ModalBody>
        {/* <div className="modal-footer">
          <Button color="secondary" onClick={toggleModal}>إغلاق</Button>
          {selectedNotification && (
            <Button color="primary" onClick={() => {
              // Add your action handler here
              toggleModal();
            }}>
              معالجة الطلب
            </Button>
          )}
        </div> */}
      </Modal>
    </React.Fragment>
  );
};

NotificationDropdown.propTypes = {
  t: PropTypes.any,
};

export default withTranslation()(NotificationDropdown);